import { useEffect, useMemo, useState } from "react";
import { Button, ConfirmModal, LabInvoicePdfFrame, Modal, showError, showSuccess } from "@common";
import {
  useClinics,
  useDiscardLabInvoiceAttachment,
  useLabInvoiceClinicOverview,
  useLabInvoiceReviewAttachments,
  useRescanLabInvoiceAttachment,
  useRetryLabInvoiceAttachmentFromEmail,
  useUpdateLabInvoiceAttachmentAssignment,
} from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { formatMoney } from "@utils/format";
import LabInvoiceDownloads from "@pages/admin/LabInvoiceDownloads";

const PAGE_SIZE = 20;

const DOCUMENT_TYPE_LABELS = {
  lab_invoice: "Lab invoice",
  lab_invoice_batch: "Lab invoice batch",
  lab_statement: "Statement",
  lab_delivery_note: "Delivery note",
  lab_credit_note: "Credit note",
  supplier_invoice: "Supplier invoice",
  other: "Other",
};
const BILLABLE_DOCUMENT_TYPES = ["lab_invoice", "lab_invoice_batch"];

function formatConfidencePercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  const percent = numeric <= 1 ? numeric * 100 : numeric;
  return `${Number.isInteger(percent) ? percent.toFixed(0) : percent.toFixed(1)}%`;
}

function getLowConfidenceLabel(breakdown) {
  if (!breakdown?.low_confidence && !breakdown?.review_required) return "";
  const percent = formatConfidencePercent(breakdown.confidence);
  return percent ? `low ${percent}` : "low";
}

function OverviewStat({ label, value, tone = "slate" }) {
  const toneClass =
    tone === "amber"
      ? "border-palette-amber-border bg-palette-amber-soft text-palette-amber-text"
      : tone === "rose"
        ? "border-palette-rose-border bg-palette-rose-soft text-palette-rose-heading"
        : tone === "emerald"
          ? "border-palette-emerald-border bg-palette-emerald-soft text-palette-emerald-hover"
          : "border-palette-slate-divider bg-white text-palette-slate-heading";

  return (
    <div className={`rounded-md border px-3 py-2 ${toneClass}`}>
      <div className="text-xs font-medium opacity-80">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value ?? 0}</div>
    </div>
  );
}

function BreakdownLine({ breakdown }) {
  const practitioner =
    breakdown.matched_practitioner_name || breakdown.practitioner_name || "Unassigned";
  const amount = breakdown.total_amount ?? breakdown.amount;
  const date = breakdown.assigned_date || breakdown.invoice_date || breakdown.service_date || "";
  const confidenceLabel = getLowConfidenceLabel(breakdown);

  return (
    <div className="grid gap-2 border-t border-palette-slate-muted py-2 text-xs text-palette-slate-badge sm:grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(80px,0.7fr)_minmax(76px,0.5fr)]">
      <div className="min-w-0">
        <div className="font-medium text-palette-slate-text">{practitioner}</div>
        {confidenceLabel && (
          <span className="mt-1 inline-flex rounded bg-palette-amber-muted px-1.5 py-0.5 text-[11px] font-semibold text-palette-amber-text">
            {confidenceLabel}
            {breakdown.reviewed_at ? " · reviewed" : ""}
          </span>
        )}
        {breakdown.patient_name && (
          <div className="truncate text-palette-slate-secondary">
            Patient: {breakdown.patient_name}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate">{breakdown.description || "No description"}</div>
        {breakdown.invoice_number && (
          <div className="truncate text-palette-slate-secondary">
            Invoice #{breakdown.invoice_number}
          </div>
        )}
      </div>
      <div>{date || "No date"}</div>
      <div className="text-right font-medium text-palette-slate-text">
        {amount !== null && amount !== undefined ? formatMoney(amount) : "No amount"}
      </div>
    </div>
  );
}

function CostBreakdowns({ attachment, isOpen, onToggle }) {
  const breakdowns = attachment.breakdowns || [];
  if (breakdowns.length === 0) return null;

  const total = breakdowns.reduce((sum, breakdown) => {
    const value = Number(breakdown.total_amount ?? breakdown.amount ?? 0);
    return Number.isNaN(value) ? sum : sum + value;
  }, 0);

  return (
    <div className="mt-3 rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 text-xs font-semibold uppercase text-palette-slate-secondary hover:text-palette-slate-heading"
      >
        <span>
          {isOpen ? "▾" : "▸"} Cost breakdowns ({breakdowns.length})
        </span>
        <span className="normal-case text-palette-slate-badge">{formatMoney(total)}</span>
      </button>
      {isOpen && (
        <div className="mt-2">
          {breakdowns.map((breakdown) => (
            <BreakdownLine key={breakdown.id} breakdown={breakdown} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClinicOverviewTab({
  data,
  isLoading,
  isError,
  error,
  onReviewClinic,
  month,
  onMonthChange,
}) {
  const rows = data?.results || [];
  const totals = data?.totals || {};

  const monthFilter = (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
      <label className="text-xs font-medium text-palette-slate-badge">
        Month received
        <input
          type="month"
          className="mt-1 block w-44 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
          value={month}
          onChange={(event) => onMonthChange(event.target.value)}
        />
      </label>
      {month ? (
        <Button type="button" variant="secondary" size="sm" onClick={() => onMonthChange("")}>
          All months
        </Button>
      ) : (
        <span className="pb-1.5 text-xs text-palette-slate-secondary">Showing all months</span>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {monthFilter}
        <div className="text-sm text-palette-slate-secondary">Loading clinic overview...</div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="space-y-4">
        {monthFilter}
        <div className="text-sm text-palette-rose-badge">
          {getApiErrorMessage(error, "Unable to load lab invoice clinic overview.")}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {monthFilter}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <OverviewStat label="Total PDFs" value={totals.total_pdfs} />
        <OverviewStat label="Billable invoices" value={totals.billable_invoices} tone="emerald" />
        <OverviewStat label="Assigned" value={totals.assigned} tone="emerald" />
        <OverviewStat label="Unassigned" value={totals.unassigned} tone="amber" />
        <OverviewStat label="Needs review" value={totals.needs_review} tone="rose" />
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-palette-slate-secondary">
          {month ? "No lab invoice PDFs received in this month." : "No lab invoice PDFs found."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-palette-slate-divider bg-white">
          <div className="grid grid-cols-[minmax(160px,1.2fr)_repeat(7,minmax(72px,0.5fr))_auto] gap-2 border-b border-palette-slate-divider bg-palette-slate-soft px-3 py-2 text-xs font-semibold uppercase text-palette-slate-secondary">
            <div>Clinic</div>
            <div className="text-right">PDFs</div>
            <div className="text-right">Invoices</div>
            <div className="text-right">Assigned</div>
            <div className="text-right">Open</div>
            <div className="text-right">Ready</div>
            <div className="text-right">Review</div>
            <div className="text-right">Failed</div>
            <div />
          </div>
          <div className="divide-y divide-palette-slate-muted">
            {rows.map((row) => (
              <div
                key={row.clinic}
                className="grid grid-cols-[minmax(160px,1.2fr)_repeat(7,minmax(72px,0.5fr))_auto] items-center gap-2 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-palette-slate-primary">{row.clinic}</div>
                  {row.clinic_name && row.clinic !== "UNMATCHED" && (
                    <div className="truncate text-xs text-palette-slate-secondary">
                      {row.clinic_name}
                    </div>
                  )}
                </div>
                <div className="text-right text-palette-slate-heading">{row.total_pdfs}</div>
                <div className="text-right text-palette-slate-heading">{row.billable_invoices}</div>
                <div className="text-right text-palette-emerald-hover">{row.assigned}</div>
                <div className="text-right text-palette-amber-heading">{row.unassigned}</div>
                <div className="text-right text-palette-emerald-hover">{row.ai_ready}</div>
                <div className="text-right text-palette-rose-heading">{row.needs_review}</div>
                <div className="text-right text-palette-rose-heading">
                  {row.upload_failed + row.ai_failed}
                </div>
                <div className="text-right">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onReviewClinic(row.clinic)}
                  >
                    Review
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LabInvoiceReviewPanel({ canAssign = false }) {
  const [activeTab, setActiveTab] = useState("review");
  const [page, setPage] = useState(1);
  const [documentType, setDocumentType] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [clinicRequestedReview, setClinicRequestedReview] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [overviewMonth, setOverviewMonth] = useState("");

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [documentType, clinicFilter, needsReview, clinicRequestedReview, search]);

  const filters = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      document_type: documentType || undefined,
      clinic: clinicFilter || undefined,
      needs_review: needsReview ? true : undefined,
      clinic_requested_review: clinicRequestedReview ? true : undefined,
      q: search || undefined,
    }),
    [page, documentType, clinicFilter, needsReview, clinicRequestedReview, search],
  );

  const { data, isLoading, isError, error } = useLabInvoiceReviewAttachments(filters, {
    enablePolling: true,
  });
  const overviewFilters = useMemo(() => ({ month: overviewMonth || undefined }), [overviewMonth]);
  const {
    data: overviewData,
    isLoading: overviewLoading,
    isError: overviewIsError,
    error: overviewError,
  } = useLabInvoiceClinicOverview(overviewFilters, {
    enabled: activeTab === "overview",
    enablePolling: true,
  });
  const updateAssignment = useUpdateLabInvoiceAttachmentAssignment();
  const rescanAttachment = useRescanLabInvoiceAttachment();
  const retryFromEmail = useRetryLabInvoiceAttachmentFromEmail();
  const discardAttachment = useDiscardLabInvoiceAttachment();
  const { data: clinicsData } = useClinics();
  const [openAttachmentId, setOpenAttachmentId] = useState(null);
  const [rescanningIds, setRescanningIds] = useState([]);
  const [rescanConfirm, setRescanConfirm] = useState(null);
  const [retryingIds, setRetryingIds] = useState([]);
  const [retryConfirm, setRetryConfirm] = useState(null);
  const [discardingIds, setDiscardingIds] = useState([]);
  const [discardConfirm, setDiscardConfirm] = useState(null);
  const [openBreakdownIds, setOpenBreakdownIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ clinic: "" });

  const attachments = data?.results || [];
  const totalCount = data?.count ?? 0;
  const totalPages = data?.total_pages ?? 1;
  const currentPage = data?.page ?? page;
  const clinics = useMemo(() => {
    const list = clinicsData?.results ?? clinicsData ?? [];
    return [...list]
      .filter((clinic) => clinic && clinic.clinic)
      .sort((a, b) => String(a.clinic).localeCompare(String(b.clinic)));
  }, [clinicsData]);

  const editingAttachment = attachments.find((item) => item.id === editingId) || null;

  function startEdit(attachment) {
    setEditingId(attachment.id);
    setDraft({ clinic: attachment.clinic || "" });
  }

  async function saveAssignment(attachmentId) {
    const clinic = draft.clinic.trim();
    if (!clinic) {
      showError("Clinic is required.");
      return;
    }
    try {
      await updateAssignment.mutateAsync({ attachmentId, data: { clinic } });
      showSuccess("Lab invoice assigned to clinic.");
      setEditingId(null);
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }

  function requestRescan(attachment) {
    setRescanConfirm({ id: attachment.id, filename: attachment.original_filename || "" });
  }

  async function confirmRescan() {
    if (!rescanConfirm) return;
    const attachmentId = rescanConfirm.id;
    setRescanConfirm(null);
    setRescanningIds((current) =>
      current.includes(attachmentId) ? current : [...current, attachmentId],
    );
    try {
      await rescanAttachment.mutateAsync(attachmentId);
      showSuccess("Lab invoice rescanned.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to rescan lab invoice."));
    } finally {
      setRescanningIds((current) => current.filter((id) => id !== attachmentId));
    }
  }

  function requestRetryFromEmail(attachment) {
    setRetryConfirm({ id: attachment.id, filename: attachment.original_filename || "" });
  }

  async function confirmRetryFromEmail() {
    if (!retryConfirm) return;
    const attachmentId = retryConfirm.id;
    setRetryConfirm(null);
    setRetryingIds((current) =>
      current.includes(attachmentId) ? current : [...current, attachmentId],
    );
    try {
      await retryFromEmail.mutateAsync(attachmentId);
      showSuccess("Lab invoice retried from email.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to retry lab invoice from email."));
    } finally {
      setRetryingIds((current) => current.filter((id) => id !== attachmentId));
    }
  }

  function requestDiscard(attachment) {
    setDiscardConfirm({ id: attachment.id, filename: attachment.original_filename || "" });
  }

  async function confirmDiscard() {
    if (!discardConfirm) return;
    const attachmentId = discardConfirm.id;
    setDiscardConfirm(null);
    setDiscardingIds((current) =>
      current.includes(attachmentId) ? current : [...current, attachmentId],
    );
    try {
      await discardAttachment.mutateAsync(attachmentId);
      if (openAttachmentId === attachmentId) setOpenAttachmentId(null);
      showSuccess("Lab invoice discarded.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to discard lab invoice."));
    } finally {
      setDiscardingIds((current) => current.filter((id) => id !== attachmentId));
    }
  }

  function reviewClinic(clinic) {
    setClinicFilter(clinic === "UNMATCHED" ? "unmatched" : clinic);
    setNeedsReview(false);
    setActiveTab("review");
  }

  function toggleBreakdowns(attachmentId) {
    setOpenBreakdownIds((current) =>
      current.includes(attachmentId)
        ? current.filter((id) => id !== attachmentId)
        : [...current, attachmentId],
    );
  }

  return (
    <section className="mb-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-palette-slate-primary">Lab Invoice Review</h3>
          <p className="text-xs text-palette-slate-secondary">
            All lab invoice PDFs{totalCount > 0 ? ` · ${totalCount} total` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {((activeTab === "review" && isLoading) ||
            (activeTab === "overview" && overviewLoading)) && (
            <span className="text-xs text-palette-slate-secondary">Loading...</span>
          )}
          {activeTab === "review" && totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage(Math.max(1, currentPage - 1))}
              >
                Prev
              </Button>
              <span className="text-xs text-palette-slate-secondary">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 flex gap-1 border-b border-palette-slate-divider">
        {[
          { key: "overview", label: "Clinic overview" },
          { key: "review", label: "Review" },
          { key: "downloads", label: "Downloads" },
        ].map((tabOption) => (
          <button
            key={tabOption.key}
            type="button"
            onClick={() => setActiveTab(tabOption.key)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium ${
              activeTab === tabOption.key
                ? "border-palette-indigo-label text-palette-indigo-badge"
                : "border-transparent text-palette-slate-secondary hover:text-palette-slate-heading"
            }`}
          >
            {tabOption.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <ClinicOverviewTab
          data={overviewData}
          isLoading={overviewLoading}
          isError={overviewIsError}
          error={overviewError}
          onReviewClinic={reviewClinic}
          month={overviewMonth}
          onMonthChange={setOverviewMonth}
        />
      )}

      {activeTab === "downloads" && <LabInvoiceDownloads />}

      {activeTab === "review" && (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-3 rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
            <label className="text-xs font-medium text-palette-slate-badge">
              Search
              <input
                type="text"
                placeholder="filename / sender / subject"
                className="mt-1 block w-56 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </label>
            <label className="text-xs font-medium text-palette-slate-badge">
              Type
              <select
                className="mt-1 block w-44 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
                value={documentType}
                onChange={(event) => setDocumentType(event.target.value)}
              >
                <option value="">All types</option>
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-palette-slate-badge">
              Clinic
              <select
                className="mt-1 block w-44 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
                value={clinicFilter}
                onChange={(event) => setClinicFilter(event.target.value)}
              >
                <option value="">All clinics</option>
                <option value="unmatched">Unmatched</option>
                {clinics.map((clinic) => (
                  <option key={clinic.clinic} value={clinic.clinic}>
                    {clinic.clinic}
                    {clinic.name ? ` — ${clinic.name}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-xs font-medium text-palette-slate-badge">
              <input
                type="checkbox"
                className="rounded border-palette-slate-ring"
                checked={needsReview}
                onChange={(event) => setNeedsReview(event.target.checked)}
              />
              Needs review only
            </label>
            <label className="flex items-center gap-1.5 text-xs font-medium text-palette-slate-badge">
              <input
                type="checkbox"
                className="rounded border-palette-slate-ring"
                checked={clinicRequestedReview}
                onChange={(event) => setClinicRequestedReview(event.target.checked)}
              />
              Clinic requested admin review
            </label>
            {(searchInput ||
              documentType ||
              clinicFilter ||
              needsReview ||
              clinicRequestedReview) && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearchInput("");
                  setDocumentType("");
                  setClinicFilter("");
                  setNeedsReview(false);
                  setClinicRequestedReview(false);
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {isError && (
            <div className="text-sm text-palette-rose-badge">
              {getApiErrorMessage(error, "Unable to load lab invoice review items.")}
            </div>
          )}

          {!isLoading && !isError && attachments.length === 0 && (
            <div className="text-sm text-palette-slate-secondary">No lab invoices found.</div>
          )}

          {attachments.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                {attachments.map((attachment) => {
                  const canViewPdf = attachment.storage_status === "uploaded";
                  const isRescanning = rescanningIds.includes(attachment.id);
                  const canRetryFromEmail =
                    !canViewPdf && attachment.email && attachment.attachment_id;
                  const isRetrying = retryingIds.includes(attachment.id);
                  const isDiscarding = discardingIds.includes(attachment.id);
                  const isNonLabInvoice =
                    attachment.document_type &&
                    !BILLABLE_DOCUMENT_TYPES.includes(attachment.document_type);
                  const statusText =
                    attachment.scan_status === "retry_from_email"
                      ? "Retry from email"
                      : attachment.storage_status !== "uploaded"
                        ? `Upload ${attachment.storage_status}`
                        : `AI ${attachment.ai_status}`;

                  return (
                    <div
                      key={attachment.id}
                      className={`rounded-md border p-3 ${
                        openAttachmentId === attachment.id
                          ? "border-palette-indigo-ring ring-1 ring-palette-indigo-hover"
                          : isNonLabInvoice
                            ? "border-palette-rose-border"
                            : "border-palette-slate-divider"
                      } ${isNonLabInvoice ? "bg-palette-rose-soft" : "bg-white"}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-medium text-palette-slate-primary">
                              {attachment.original_filename}
                            </span>
                            {!isNonLabInvoice && (
                              <span className="rounded bg-palette-amber-muted px-2 py-0.5 text-xs font-medium text-palette-amber-text">
                                {statusText}
                              </span>
                            )}
                            {attachment.document_type && (
                              <span
                                className={`rounded px-2 py-0.5 text-xs font-medium ${
                                  BILLABLE_DOCUMENT_TYPES.includes(attachment.document_type)
                                    ? "bg-palette-emerald-muted text-palette-emerald-text"
                                    : "bg-palette-rose-muted text-palette-rose-text"
                                }`}
                              >
                                {DOCUMENT_TYPE_LABELS[attachment.document_type] ||
                                  attachment.document_type}
                              </span>
                            )}
                          </div>
                          {attachment.document_type === "lab_invoice_batch" && (
                            <div className="mt-1 rounded-md border border-palette-rose-border bg-palette-rose-soft px-2 py-1 text-[11px] text-palette-rose-heading">
                              Batch (multiple invoices) — ask the sender to split it, one invoice
                              per PDF.
                            </div>
                          )}
                          <div className="mt-1 text-xs text-palette-slate-secondary">
                            From {attachment.sender_name ? `${attachment.sender_name} ` : ""}
                            {attachment.sender_email ? (
                              <span className="text-palette-slate-heading">
                                &lt;{attachment.sender_email}&gt;
                              </span>
                            ) : (
                              !attachment.sender_name && "unknown"
                            )}{" "}
                            · {attachment.email_subject || "(no subject)"}
                          </div>
                          <div className="mt-1 text-xs text-palette-slate-secondary">
                            Clinic:{" "}
                            {attachment.clinic ? (
                              <span className="font-medium text-palette-slate-heading">
                                {attachment.clinic}
                                {attachment.clinic_name ? ` — ${attachment.clinic_name}` : ""}
                              </span>
                            ) : (
                              <span className="font-medium text-palette-amber-heading">
                                Unassigned
                              </span>
                            )}
                          </div>
                          {!isNonLabInvoice && (attachment.review_reasons || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {attachment.review_reasons.map((reason) => (
                                <span
                                  key={reason}
                                  className="rounded bg-palette-amber-soft px-1.5 py-0.5 text-[11px] font-medium text-palette-amber-heading ring-1 ring-inset ring-palette-amber-border"
                                >
                                  {reason}
                                </span>
                              ))}
                            </div>
                          )}
                          {!isNonLabInvoice && (
                            <CostBreakdowns
                              attachment={attachment}
                              isOpen={openBreakdownIds.includes(attachment.id)}
                              onToggle={() => toggleBreakdowns(attachment.id)}
                            />
                          )}
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          {canViewPdf && (
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() =>
                                setOpenAttachmentId(
                                  openAttachmentId === attachment.id ? null : attachment.id,
                                )
                              }
                            >
                              {openAttachmentId === attachment.id ? "Hide PDF" : "View PDF"}
                            </Button>
                          )}
                          {canAssign && canViewPdf && (
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={isRescanning}
                              onClick={() => requestRescan(attachment)}
                            >
                              {isRescanning ? "Rescanning..." : "Rescan"}
                            </Button>
                          )}
                          {canAssign && canRetryFromEmail && (
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={isRetrying || isRescanning}
                              onClick={() => requestRetryFromEmail(attachment)}
                            >
                              {isRetrying ? "Retrying..." : "Retry from email"}
                            </Button>
                          )}
                          {canAssign && (
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => startEdit(attachment)}
                            >
                              Assign clinic
                            </Button>
                          )}
                          {canAssign && (
                            <Button
                              type="button"
                              variant="danger"
                              disabled={isDiscarding || isRescanning || isRetrying}
                              onClick={() => requestDiscard(attachment)}
                            >
                              {isDiscarding ? "Discarding..." : "Discard"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="lg:sticky lg:top-4 lg:self-start">
                {openAttachmentId ? (
                  <>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="truncate text-xs font-medium text-palette-slate-badge">
                        {attachments.find((item) => item.id === openAttachmentId)
                          ?.original_filename || "PDF preview"}
                      </span>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setOpenAttachmentId(null)}
                      >
                        Close
                      </Button>
                    </div>
                    <div className="h-[78vh] overflow-hidden rounded border border-palette-slate-divider bg-palette-slate-muted">
                      <LabInvoicePdfFrame attachmentId={openAttachmentId} className="min-h-full" />
                    </div>
                  </>
                ) : (
                  <div className="flex h-[78vh] items-center justify-center rounded border border-dashed border-palette-slate-ring bg-white text-sm text-palette-slate-placeholder">
                    Select &ldquo;View PDF&rdquo; to preview a lab invoice here.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={Boolean(rescanConfirm)}
        onClose={() => setRescanConfirm(null)}
        onConfirm={confirmRescan}
        title="Rescan lab invoice?"
        message={
          rescanConfirm?.filename
            ? `Re-run AI extraction on "${rescanConfirm.filename}"? This overwrites the current suggested clinic, practitioner, and cost breakdowns.`
            : "Re-run AI extraction on this lab invoice? This overwrites the current suggested clinic, practitioner, and cost breakdowns."
        }
        confirmText="Rescan"
        confirmVariant="secondary"
      />

      <ConfirmModal
        isOpen={Boolean(retryConfirm)}
        onClose={() => setRetryConfirm(null)}
        onConfirm={confirmRetryFromEmail}
        title="Retry from email?"
        message={
          retryConfirm?.filename
            ? `Re-download "${retryConfirm.filename}" from the original Outlook email, then upload and scan it again.`
            : "Re-download this PDF from the original Outlook email, then upload and scan it again."
        }
        confirmText="Retry"
        confirmVariant="secondary"
      />

      <ConfirmModal
        isOpen={Boolean(discardConfirm)}
        onClose={() => setDiscardConfirm(null)}
        onConfirm={confirmDiscard}
        title="Discard lab invoice?"
        message={
          discardConfirm?.filename
            ? `Discard "${discardConfirm.filename}"? It will no longer appear for review.`
            : "Discard this lab invoice? It will no longer appear for review."
        }
        confirmText="Discard"
        confirmVariant="danger"
      />

      <Modal
        isOpen={Boolean(editingId)}
        onClose={() => setEditingId(null)}
        title="Assign clinic"
        size="sm"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setEditingId(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="success"
              disabled={updateAssignment.isPending || !draft.clinic.trim()}
              onClick={() => saveAssignment(editingId)}
            >
              {updateAssignment.isPending ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        {editingAttachment && (
          <p className="mb-3 truncate text-sm text-palette-slate-secondary">
            {editingAttachment.original_filename}
          </p>
        )}
        <label className="block text-sm font-medium text-palette-slate-heading">
          Clinic
          <select
            className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-2 text-sm"
            value={draft.clinic}
            onChange={(event) =>
              setDraft((current) => ({ ...current, clinic: event.target.value }))
            }
          >
            <option value="">Select clinic…</option>
            {clinics.map((clinic) => (
              <option key={clinic.clinic} value={clinic.clinic}>
                {clinic.clinic}
                {clinic.name ? ` — ${clinic.name}` : ""}
              </option>
            ))}
          </select>
        </label>
      </Modal>
    </section>
  );
}
